import type {
  FoxKifuAiAnalysis,
  FoxKifuObservationTimelineResponse,
  GameReviewFrame,
  StoneColor,
} from '@wx-weiqi/contracts';
import { observationFrameKey, type ObservationWinRatePoint } from '../domain/observation-analysis';
import type { GameReplayStore } from './game-replay-store';

export interface ObservationTimelineContext {
  readonly gameId: string;
  readonly stateVersion: number;
  readonly configurationKey: string;
  readonly rulesKey: string;
  readonly currentPlayer: StoneColor;
  readonly frames: readonly GameReviewFrame[];
  readonly terminal?: boolean;
}
export interface ObservationTimelineState {
  readonly context: ObservationTimelineContext | null;
  readonly data: FoxKifuObservationTimelineResponse['data'] | null;
  readonly points: readonly ObservationWinRatePoint[];
  readonly error: string | null;
  readonly loading: boolean;
}

/** A display projection only: the server restores moves, schedules work, and owns persistent results. */
export class ObservationTimelineSession {
  private state: ObservationTimelineState = {
    context: null,
    data: null,
    points: [],
    error: null,
    loading: false,
  };
  private enabled = false;
  private restoreSaved = false;
  private foregroundReady = true;
  private generation = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly listeners = new Set<() => void>();
  constructor(
    private readonly client:
      | (Pick<GameReplayStore, 'loadObservationTimeline'> &
          Partial<Pick<GameReplayStore, 'readObservationTimeline'>>)
      | undefined,
  ) {}
  readonly snapshot = () => this.state;
  readonly subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  setContext(
    context: ObservationTimelineContext | null,
    enabled: boolean,
    restoreSaved = false,
  ): void {
    const old = this.state.context;
    const sameContext =
      old?.gameId === context?.gameId &&
      old?.stateVersion === context?.stateVersion &&
      old?.configurationKey === context?.configurationKey &&
      old?.rulesKey === context?.rulesKey &&
      old?.terminal === context?.terminal;
    if (sameContext && this.enabled === enabled && this.restoreSaved === restoreSaved) return;
    const needsRead =
      restoreSaved &&
      (!sameContext ||
        !this.restoreSaved ||
        (this.enabled && !enabled) ||
        this.state.loading ||
        this.state.data === null);
    this.invalidate();
    this.enabled = enabled;
    this.restoreSaved = restoreSaved;
    // A restored curve may belong to an older model/account version. Until the first
    // POST identifies the active profile, never add fresh foreground points to it.
    this.foregroundReady = !restoreSaved;
    let common = 0;
    if (
      old !== null &&
      context !== null &&
      old.gameId === context.gameId &&
      old.configurationKey === context.configurationKey &&
      old.rulesKey === context.rulesKey
    ) {
      while (
        common < Math.min(old.frames.length, context.frames.length) &&
        observationFrameKey(old.frames[common]!) === observationFrameKey(context.frames[common]!)
      )
        common++;
    }
    const points = this.state.points.filter(
      (point) =>
        point.moveNumber < common &&
        (old?.frames[point.moveNumber + 1]?.color ?? old?.currentPlayer) ===
          (context?.frames[point.moveNumber + 1]?.color ?? context?.currentPlayer),
    );
    const retained =
      this.state.data?.points.filter((point) =>
        points.some((p) => p.moveNumber === point.moveNumber),
      ) ?? [];
    const data =
      common > 0 && context !== null && this.state.data !== null
        ? {
            ...this.state.data,
            total: context.frames.length,
            completed: retained.length,
            points: retained,
            pendingMoveNumber: null,
            error: null,
          }
        : null;
    this.publish({ context, points, data, error: null, loading: false });
    if (enabled && context !== null) void this.refresh(false);
    else if (needsRead && context !== null) void this.readSaved();
  }

  readonly recordForeground = (moveNumber: number, analysis: FoxKifuAiAnalysis | null): void => {
    const context = this.state.context;
    const frame = context?.frames[moveNumber];
    if (
      !this.enabled ||
      !this.foregroundReady ||
      context === null ||
      frame === undefined ||
      analysis?.result == null ||
      analysis.recordId !== context.gameId
    )
      return;
    const point = {
      gameId: context.gameId,
      moveNumber,
      stonesKey: observationFrameKey(frame),
      visits: analysis.result.bestMoves.reduce(
        (maximum, move) => Math.max(maximum, move.visits),
        0,
      ),
      blackWinRateBps: analysis.result.winRateBps,
      blackScoreLeadHalfPoints: analysis.result.scoreLeadHalfPoints,
    };
    this.publish({
      ...this.state,
      points: [...this.state.points.filter((p) => p.moveNumber !== moveNumber), point].sort(
        (a, b) => a.moveNumber - b.moveNumber,
      ),
    });
  };

  readonly retry = () => {
    this.invalidate();
    if (this.enabled) void this.refresh(true);
    else if (this.restoreSaved) void this.readSaved();
  };
  readonly stop = () => {
    this.enabled = false;
    this.invalidate();
  };
  private invalidate() {
    this.generation++;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }
  private async readSaved(): Promise<void> {
    const context = this.state.context;
    if (
      !this.restoreSaved ||
      this.client?.readObservationTimeline === undefined ||
      context === null
    )
      return;
    const generation = this.generation;
    // Once AI is off, only label points confirmed by the server as saved. A streamed
    // foreground result may still be waiting for its durable write.
    this.publish({
      ...this.state,
      points: this.projectPoints(this.state.data?.points ?? [], context),
      loading: true,
      error: null,
    });
    try {
      const response = await this.client.readObservationTimeline(
        context.gameId,
        context.stateVersion,
      );
      if (generation !== this.generation) return;
      if (response.gameId !== context.gameId || response.stateVersion !== context.stateVersion)
        throw new Error('Saved timeline context mismatch');
      this.acceptResponse(response, context, false);
    } catch {
      if (generation === this.generation)
        this.publish({
          ...this.state,
          loading: false,
          error: '已保存的胜率暂时无法读取，请重试。',
        });
    }
  }
  private projectPoints(
    points: FoxKifuObservationTimelineResponse['data']['points'],
    context: ObservationTimelineContext,
  ) {
    return points.flatMap((point) => {
      const frame = context.frames[point.moveNumber];
      return frame === undefined
        ? []
        : [{ ...point, gameId: context.gameId, stonesKey: observationFrameKey(frame) }];
    });
  }
  private acceptResponse(
    response: FoxKifuObservationTimelineResponse,
    context: ObservationTimelineContext,
    preserveForeground = true,
  ) {
    const points = this.projectPoints(response.data.points, context);
    // Preserve only points from the same profile; a restored historical profile must
    // be replaced atomically when current computation uses another profile.
    const merged = new Map(
      preserveForeground &&
        (this.state.data?.profileKey === response.data.profileKey || this.state.data === null)
        ? this.state.points.map((p) => [p.moveNumber, p])
        : [],
    );
    for (const point of points) {
      if ((merged.get(point.moveNumber)?.visits ?? -1) <= point.visits)
        merged.set(point.moveNumber, point);
    }
    this.publish({
      ...this.state,
      data: response.data,
      points: [...merged.values()].sort((a, b) => a.moveNumber - b.moveNumber),
      error: response.data.error,
      loading: false,
    });
  }
  private async refresh(retry: boolean): Promise<void> {
    const context = this.state.context;
    if (!this.enabled || this.client === undefined || context === null) return;
    const generation = this.generation;
    try {
      const response = await this.client.loadObservationTimeline(
        context.gameId,
        context.stateVersion,
        retry,
      );
      if (
        generation !== this.generation ||
        response.gameId !== context.gameId ||
        response.stateVersion !== context.stateVersion
      )
        return;
      this.foregroundReady = true;
      this.acceptResponse(response, context);
    } catch {
      if (generation === this.generation)
        this.publish({ ...this.state, error: '历史胜率暂时无法更新，稍后自动重试。' });
    } finally {
      const complete =
        context.terminal === true &&
        this.state.data !== null &&
        this.state.data.completed === context.frames.length &&
        this.state.data.pendingMoveNumber === null &&
        this.state.error === null;
      if (generation === this.generation && this.enabled && !complete)
        this.timer = setTimeout(() => {
          this.timer = null;
          void this.refresh(false);
        }, 3000);
    }
  }
  private publish(state: ObservationTimelineState) {
    this.state = state;
    for (const listener of this.listeners) listener();
  }
}
